import { Badge } from '@/components/ui/badge';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn, formatIDR, formatTimeRange, hexToRgba, isEmpty } from '@/lib/utils';
import { ThemeColorSchemeType, ThemePromotionType } from '@/stores/builder';
import { BasePromotion } from '@/types/http/promotion/common';
import { TicketPercent } from 'lucide-react';
import { useRef } from 'react';

type CardPromotionProps = {
  isSelected: boolean;
  isDisabled: boolean;
  percentage: number;
  promotion: BasePromotion;
  isEligiblePromo: boolean;
  grandTotal: number;
  handleSelectPromo: (promotion: BasePromotion) => void;
  colorScheme?: ThemeColorSchemeType;
  theme?: ThemePromotionType;
  preview?: boolean;
};

export default function CardPromotion({
  isSelected,
  isDisabled,
  percentage,
  promotion,
  isEligiblePromo,
  grandTotal,
  handleSelectPromo,
  colorScheme,
  theme,
  preview = false,
}: CardPromotionProps) {
  const checkboxRef = useRef<HTMLButtonElement>(null);

  const handleCardClick = () => {
    if (!isDisabled) {
      checkboxRef.current?.click();
    }
  };

  const tnc = promotion.promotion_tnc;

  const timeRange = tnc?.validSchedule?.time ? formatTimeRange(tnc.validSchedule.time) : null;

  const dayMap: Record<string, string> = {
    Mon: 'Monday',
    Tue: 'Tuesday',
    Wed: 'Wednesday',
    Thu: 'Thursday',
    Fri: 'Friday',
    Sat: 'Saturday',
    Sun: 'Sunday',
  };

  const allDayShort = Object.keys(dayMap);

  const daysString = tnc?.validSchedule?.days || '';
  const daysArrayShort = daysString
    .split(',')
    .map((day) => day.trim())
    .filter((day) => allDayShort.includes(day));

  const daysArrayFull = daysArrayShort.map((d) => dayMap[d]);

  let daysDisplay = '';
  if (daysArrayFull.length === 1) {
    daysDisplay = `Every ${daysArrayFull[0]}`;
  } else if (allDayShort.every((day) => daysArrayShort.includes(day))) {
    daysDisplay = 'Every Day';
  } else {
    daysDisplay = daysArrayFull.join(', ');
  }

  return (
    <div
      key={promotion.promotion_id}
      className={cn(
        'flex w-full flex-col items-center justify-between gap-2 rounded-md border-2 bg-[--background] p-4 transition-all',
        isDisabled ? 'cursor-not-allowed opacity-50' : 'cursor-pointer',
        isSelected && isEmpty(theme?.colorScheme) ? 'border-primary' : 'border-gray-200',
        isSelected && !isEmpty(theme?.colorScheme) && 'border-[var(--border)]'
      )}
      onClick={handleCardClick}
      style={
        {
          '--background': hexToRgba(theme?.colorScheme?.background, 0.1),
          '--border': theme?.colorScheme?.background,
          '--text-primary': theme?.colorScheme?.text,
          '--text-secondary': theme?.colorScheme?.textSecondary || '#000',
          '--btn-background': theme?.colorScheme?.btnSolidBackgroud,
          '--btn-text': theme?.colorScheme?.btnSolidlabel,
        } as React.CSSProperties
      }
    >
      <div className='flex w-full items-center justify-between gap-5'>
        <div
          className={cn(
            'flex w-full items-center gap-3',
            isEmpty(theme?.colorScheme) ? 'text-gray-800' : 'text-[var(--text-primary)]'
          )}
        >
          <div className='flex items-center'>
            <TicketPercent className='h-5 w-5' />
          </div>
          <div className='flex flex-col gap-1 text-xs font-normal'>
            <h3 className='line-clamp-1 text-sm font-bold'>{promotion.promotion_name}</h3>
            {!(tnc?.typeSpending === 'product' && tnc?.minPurchase === 0) && (
              <span className='text-[10px] text-[var(--text-secondary)]'>
                Min. Purchase{' '}
                {tnc?.typeSpending === 'amount' || promotion.minimum_spending > 1000
                  ? `${formatIDR(promotion.minimum_spending)}`
                  : `${promotion.minimum_spending} pcs`}
              </span>
            )}
          </div>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <Checkbox
            ref={checkboxRef}
            className={cn(
              'size-5 rounded-full border shadow-none transition-colors',
              !isEmpty(theme?.colorScheme)
                ? 'border-[var(--background)] bg-white text-[var(--btn-text)] data-[state=checked]:bg-[var(--btn-background)]'
                : 'text-white data-[state=checked]:border-primary data-[state=checked]:bg-primary'
            )}
            style={
              !isEmpty(colorScheme)
                ? ({
                    '--checkbox-bg-color': colorScheme?.btnSolidBackgroud,
                    '--checkbox-text-color': colorScheme?.btnSolidlabel,
                  } as React.CSSProperties)
                : undefined
            }
            checked={isSelected}
            onCheckedChange={() => handleSelectPromo(promotion)}
            disabled={isDisabled}
            isCustom
          />
        </div>
      </div>

      <div className='relative w-full'>
        <div className='mt-2 h-2 w-full overflow-hidden rounded-full bg-gray-200'>
          <div
            className='h-2 transition-all duration-300'
            style={{ width: `${percentage}%`, backgroundColor: theme?.progressBar || '#3B82F6' }}
          />
        </div>
        <div className='relative my-2 flex items-center gap-2'>
          {preview ? (
            <div className='flex items-center gap-1'>
              <Badge
                variant='outline'
                className='rounded border-orange-300 bg-orange-100 px-1 py-0 text-[10px] font-medium text-orange-700'
              >
                Ready to Claim
              </Badge>
              <Badge
                variant='outline'
                className='rounded border-yellow-300 bg-yellow-100 px-1 py-0 text-[10px] font-medium text-yellow-800'
              >
                {`Spend ${formatIDR(promotion?.minimum_spending - grandTotal)} more`}
              </Badge>
            </div>
          ) : isEligiblePromo ? (
            <Badge
              variant='outline'
              className='rounded border-orange-300 bg-orange-100 px-1 py-0 text-[10px] font-medium text-orange-700'
            >
              Ready to Claim
            </Badge>
          ) : (
            <Badge
              variant='outline'
              className='rounded border-yellow-300 bg-yellow-100 px-1 py-0 text-[10px] font-medium text-yellow-800'
            >
              {`Spend ${formatIDR(promotion?.minimum_spending - grandTotal)} more`}
            </Badge>
          )}
          {preview ? (
            <>
              <Badge
                variant='outline'
                className='rounded border-blue-300 bg-blue-100 px-1 py-0 text-[10px] font-medium text-blue-700'
              >
                Stackable
              </Badge>
              <Badge
                variant='outline'
                className='rounded border-gray-300 bg-gray-100 px-1 py-0 text-[10px] font-medium text-gray-700'
              >
                Non-stackable
              </Badge>
            </>
          ) : promotion?.is_overlap ? (
            <Badge
              variant='outline'
              className='rounded border-blue-300 bg-blue-100 px-1 py-0 text-[10px] font-medium text-blue-700'
            >
              Stackable
            </Badge>
          ) : (
            <Badge
              variant='outline'
              className='rounded border-gray-300 bg-gray-100 px-1 py-0 text-[10px] font-medium text-gray-700'
            >
              Non-stackable
            </Badge>
          )}
          {promotion.use_voucher && (
            <Badge
              variant='outline'
              className='rounded border-purple-300 bg-purple-100 px-1 py-0 text-[10px] font-medium text-purple-700'
            >
              Redeem Voucher
            </Badge>
          )}
        </div>

        <Popover>
          <PopoverTrigger asChild>
            <div className='inline-flex items-center' onClick={(e) => e.stopPropagation()}>
              <span className='cursor-pointer text-xs text-black underline'>T&Cs apply</span>
            </div>
          </PopoverTrigger>
          <PopoverContent
            className='z-[9991] max-h-[300px] w-full max-w-sm space-y-2 overflow-y-auto break-words text-xs lg:max-w-lg'
            style={{ color: theme?.colorScheme?.textSecondary || '#6B7280' }}
          >
            <div>
              <strong>Promo:</strong> {tnc?.reward}
            </div>
            {tnc?.rules && tnc.rules.length > 0 && (
              <div>
                <strong>Rules:</strong>
                <ul className='list-disc pl-4'>
                  {tnc.rules.map((rule, idx) => (
                    <li key={idx}>{rule}</li>
                  ))}
                </ul>
              </div>
            )}
            {!(tnc?.typeSpending === 'product' && tnc?.minPurchase === 0) && (
              <div>
                <strong>Min {tnc?.typeSpending === 'amount' ? 'Purchase' : 'Qty'}:</strong>{' '}
                {tnc?.typeSpending === 'amount'
                  ? formatIDR(tnc?.minPurchase)
                  : `${tnc?.minPurchase} pcs`}
              </div>
            )}
            <div>
              <strong>Multiplier:</strong> {tnc?.multiplier ? 'Valid' : 'Not Valid'}
            </div>
            {tnc?.rewardSimulation && tnc.rewardSimulation.length > 0 && (
              <div>
                <strong>Reward Simulation:</strong>
                <ul className='list-disc pl-4'>
                  {tnc?.rewardSimulation.map((item, idx) => (
                    <li key={idx}>
                      {item.product_rules?.length > 0 ? (
                        <>
                          {item.product_rules?.length > 0 && (
                            <div className='mb-1'>
                              <span className='font-medium'>Product Rules:</span>{' '}
                              {item.product_rules?.join(', ')}
                            </div>
                          )}
                          <div>
                            <span className='font-medium'>Rewards:</span> {item.rewards.join(' + ')}
                          </div>
                        </>
                      ) : (
                        <>
                          {tnc?.typeSpending === 'amount'
                            ? `Spend ${formatIDR(item.min_spending)}`
                            : `Buy ${item.min_spending} pcs`}{' '}
                          → {Array.isArray(item.rewards) ? item.rewards.join(', ') : ''}
                        </>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            <div>
              <strong>Valid Schedule:</strong>
              {tnc?.validSchedule?.period && <div>📅 {tnc.validSchedule.period}</div>}
              {tnc?.validSchedule?.applicable_type === 'date' && tnc.validSchedule.date ? (
                <div>
                  📆 Applies on days {tnc.validSchedule.date.start_date}–
                  {tnc.validSchedule.date.end_date} of each month
                </div>
              ) : (
                tnc?.validSchedule.days !== '""' &&
                daysArrayFull.length > 0 && <div>🗓️ {daysDisplay}</div>
              )}
              {timeRange && timeRange !== '00:00 - 00:00' && <div>⏰ {timeRange}</div>}
            </div>
          </PopoverContent>
        </Popover>
      </div>
    </div>
  );
}
